
/**
 * 价格变化百分比数据结构
 */
export interface PriceChangePercentage {
  time_period: string;
  percentage?: number;
}

/**
 * OHLC 数据结构
 */
export interface OHLC {
  /** timestamp of OLHC */
  time?: number;
  /** open price of OLHC */
  open?: number;
  /** high price of OLHC */
  high?: number;
  /** low price of OLHC */
  low?: number;
  /** close price of OLHC */
  close?: number;
  /** volume of OLHC */
  volume?: number;
  /** volume of OLHC in USD */
  volume_usd?: number;
}

/**
 * 构建股票价格变化百分比数据的参数
 */
export interface BuildPriceChangeDataParams {
  /** OHLC 数据列表（已按时间倒序排列） */
  ohlcvData: OHLC[];
  /** 当前价格 */
  currentPrice: number;
  /** 年初开盘价 */
  openCurrentYear: number;
  offsetDaysList: { label: string; offset: number }[];
}

export interface Build1DPriceChangeDataParams {
  currentPrice: number;
  closePrice: number;
}

/**
 * 构建股票价格变化百分比数据
 *
 * @param params - 构建参数
 * @returns 价格变化百分比数据数组
 *
 * @example
 * ```typescript
 * const priceChangeData = buildPriceChangeData({
 *   ohlcvData: [...],
 *   currentPrice: 100.50,
 *   openCurrentYear: 95.00
 * });
 * ```
 */
export function buildPriceChangeData1D(
  params: Build1DPriceChangeDataParams,
): number {
  const { currentPrice, closePrice } = params;
  const percentage = ((currentPrice - closePrice) / closePrice) * 100;
  return percentage;
}

export function buildPriceChangeData(
  params: BuildPriceChangeDataParams,
): PriceChangePercentage[] {
  const {
    ohlcvData,
    currentPrice,
    openCurrentYear,
    offsetDaysList: customOffsetDaysList,
  } = params;

  // 显示顺序要求：1D、5D、30D、6M、YTD、1Y
  // 其中 6M 按 180 个交易日计算，1Y 按 365 个交易日计算
  // 先构建包含 1Y 的固定周期，稍后把 YTD 插入到 6M 后面
  const ohlcvDataReverse = [...ohlcvData].reverse();
  const offsetDaysList =
    customOffsetDaysList ||
    ([
      { label: "1D", offset: 1 },
      { label: "5D", offset: 5 },
      { label: "30D", offset: 30 },
      { label: "6M", offset: 180 },
      { label: "1Y", offset: 365 },
    ] as const);

  const result: PriceChangePercentage[] = offsetDaysList.map(
    ({ label, offset }): PriceChangePercentage => {
      const index = offset;
      if (
        index >= 0 &&
        index < ohlcvDataReverse.length &&
        ohlcvDataReverse[index]?.close &&
        currentPrice > 0
      ) {
        const closePrice = Number(ohlcvDataReverse[index].close || 0);
        const percentage = ((currentPrice - closePrice) / closePrice) * 100;
        return { time_period: label, percentage };
      }
      return { time_period: label, percentage: undefined };
    },
  );


  // YTD：使用年初开盘价，插入到 6M 后面
  const sixMIndex = result.findIndex((i) => i.time_period === "6M");
  const insertIndex = sixMIndex >= 0 ? sixMIndex + 1 : result.length - 1;
  if (openCurrentYear > 0 && currentPrice > 0) {
    const yearPercentage =
      ((currentPrice - openCurrentYear) / openCurrentYear) * 100;
    result.splice(insertIndex, 0, {
      time_period: "YTD",
      percentage: yearPercentage,
    });
  } else {
    result.splice(insertIndex, 0, {
      time_period: "YTD",
      percentage: undefined,
    });
  }

  return result;
}
